import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { PHONE_DISPLAY, PHONE_TEL } from "./Nav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const serviceOptions = [
  "AC Repair",
  "Heating / Furnace Repair",
  "HVAC Installation",
  "Maintenance Plan",
  "Ductless Mini-Split",
  "Indoor Air Quality",
  "Emergency Service",
  "Something Else",
];

const inputCls =
  "w-full bg-[#121212] border border-white/15 focus:border-[#FF6B00] focus:outline-none text-white placeholder:text-zinc-600 px-5 py-4 rounded-sm transition-colors duration-300 text-base";

export default function QuoteForm() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", date: "", time_slot: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.service) {
      toast.error("Please fill in your name, phone, and the service you need.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("Request received — we'll call you back shortly.");
      setForm({ name: "", phone: "", email: "", service: "", date: "", time_slot: "", message: "" });
    } catch {
      toast.error(`Something went wrong. Please call us at ${PHONE_DISPLAY}.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="quote"
      className="relative py-24 md:py-32 border-b border-white/10"
      data-testid="quote-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-16 lg:gap-24">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7 }}
            className="font-mono-tech text-xs uppercase tracking-[0.3em] text-[#FF6B00] mb-6"
          >
            Free Estimate
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-display font-bold uppercase tracking-tighter text-4xl md:text-5xl lg:text-6xl leading-tight mb-8"
          >
            Tell us what's
            <br />
            <span className="text-[#FF6B00]">not working.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="text-zinc-400 text-base md:text-lg max-w-md mb-10"
          >
            Drop your details and we'll call you back with a straight answer and a
            free estimate. Rather talk now? We're open 24/7 at{" "}
            <a href={PHONE_TEL} className="text-[#FF6B00] hover:underline" data-testid="quote-phone-link">
              {PHONE_DISPLAY}
            </a>
            .
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="hidden lg:block border border-white/10 rounded-sm overflow-hidden"
          >
            <img
              src="https://images.unsplash.com/photo-1660330589827-da8ab7dd3c02?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwyfHxIVkFDJTIwdGVjaG5pY2lhbiUyMGluZHVzdHJpYWx8ZW58MHx8fHwxNzg3OTIyODc3fDA&ixlib=rb-4.1.0&q=85"
              alt="HVAC technician at work"
              className="w-full h-72 object-cover"
              data-testid="quote-side-image"
            />
          </motion.div>
        </div>

        <motion.form
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          onSubmit={submit}
          className="space-y-5 self-center"
          data-testid="quote-form"
        >
          <div>
            <label htmlFor="quote-name" className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">
              Your Name *
            </label>
            <input
              id="quote-name"
              type="text"
              value={form.name}
              onChange={set("name")}
              placeholder="Jane Doe"
              className={inputCls}
              data-testid="quote-name-input"
            />
          </div>
          <div>
            <label htmlFor="quote-phone" className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">
              Phone Number *
            </label>
            <input
              id="quote-phone"
              type="tel"
              value={form.phone}
              onChange={set("phone")}
              placeholder="(865) 555-0123"
              className={inputCls}
              data-testid="quote-phone-input"
            />
          </div>
          <div>
            <label htmlFor="quote-email" className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">
              Email (optional — get a confirmation)
            </label>
            <input
              id="quote-email"
              type="email"
              value={form.email}
              onChange={set("email")}
              placeholder="you@email.com"
              className={inputCls}
              data-testid="quote-email-input"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label htmlFor="quote-date" className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">
                Preferred Date
              </label>
              <input
                id="quote-date"
                type="date"
                value={form.date}
                onChange={set("date")}
                className={`${inputCls} [color-scheme:dark]`}
                data-testid="quote-date-input"
              />
            </div>
            <div>
              <label htmlFor="quote-timeslot" className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">
                Time Slot
              </label>
              <select
                id="quote-timeslot"
                value={form.time_slot}
                onChange={set("time_slot")}
                className={`${inputCls} appearance-none ${form.time_slot ? "" : "text-zinc-600"}`}
                data-testid="quote-timeslot-select"
              >
                <option value="" className="bg-[#121212] text-white">Any time</option>
                <option value="As soon as possible" className="bg-[#121212] text-white">As soon as possible</option>
                <option value="Morning (8 AM – 12 PM)" className="bg-[#121212] text-white">Morning (8 AM – 12 PM)</option>
                <option value="Afternoon (12 – 4 PM)" className="bg-[#121212] text-white">Afternoon (12 – 4 PM)</option>
                <option value="Evening (4 – 8 PM)" className="bg-[#121212] text-white">Evening (4 – 8 PM)</option>
              </select>
            </div>
          </div>
          <div>
            <label htmlFor="quote-service" className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">
              Service Needed *
            </label>
            <select
              id="quote-service"
              value={form.service}
              onChange={set("service")}
              className={`${inputCls} appearance-none ${form.service ? "" : "text-zinc-600"}`}
              data-testid="quote-service-select"
            >
              <option value="" disabled>
                Select a service…
              </option>
              {serviceOptions.map((s) => (
                <option key={s} value={s} className="bg-[#121212] text-white">
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="quote-message" className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">
              Details (optional)
            </label>
            <textarea
              id="quote-message"
              value={form.message}
              onChange={set("message")}
              placeholder="e.g. AC stopped blowing cold air last night…"
              rows={4}
              className={`${inputCls} resize-none`}
              data-testid="quote-message-input"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 bg-[#FF6B00] hover:bg-[#E66000] disabled:opacity-60 text-[#050505] font-display font-bold uppercase tracking-wide text-lg px-8 py-5 rounded-sm transition-colors duration-300"
            data-testid="quote-submit-button"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" strokeWidth={2.5} />
            )}
            {loading ? "Sending…" : "Request Free Estimate"}
          </button>
          <p className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-600 text-center">
            No spam. No pressure. Just a call back from a local tech.
          </p>
        </motion.form>
      </div>
    </section>
  );
}
